import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { extractErrorMessage } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

// Spreadsheet-style column types (a column is a field of the collection).
// The full set, with advanced options, stays on the Fields page.
const COLUMN_TYPES = [
  { value: "text", label: "Text" },
  { value: "number", label: "Number", unit: true },
  { value: "currency", label: "Price / money" },
  { value: "dropdown", label: "Dropdown (pick one)", options: true },
  { value: "multi_select", label: "Multi-select (pick several)", options: true },
  { value: "boolean", label: "Yes / No" },
  { value: "date", label: "Date" },
  { value: "image", label: "Photo" },
  { value: "file", label: "File" },
  { value: "longtext", label: "Long text / notes" },
  { value: "phone", label: "Phone" },
  { value: "email", label: "Email" },
  { value: "url", label: "Link (URL)" },
];

// Field keys must match ^[a-z][a-z0-9_]*$ (max 64).
export function keyFromLabel(label) {
  let k = (label || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
  if (!k) k = "column";
  if (!/^[a-z]/.test(k)) k = `f_${k}`;
  return k.slice(0, 60);
}

/**
 * "Add column" straight from the records table: name + type, and the new
 * column appears immediately. Creates a field on the collection (in shared-
 * fields mode the server also adds it to the other catalogues).
 */
export function AddColumnDialog({ open, onOpenChange, entityTypeId, existingKeys = [], onCreated }) {
  const [label, setLabel] = useState("");
  const [type, setType] = useState("text");
  const [unit, setUnit] = useState("");
  const [optionsText, setOptionsText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) { setLabel(""); setType("text"); setUnit(""); setOptionsText(""); }
  }, [open]);

  const spec = COLUMN_TYPES.find((t) => t.value === type) || COLUMN_TYPES[0];
  const options = optionsText.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
  const canSave = label.trim() && (!spec.options || options.length > 0) && !busy;

  const submit = async (e) => {
    e?.preventDefault();
    if (!canSave) return;
    setBusy(true);
    const base = keyFromLabel(label.trim());
    const taken = new Set(existingKeys);
    const body = {
      label: label.trim(),
      type,
      ...(spec.unit && unit.trim() ? { unit: unit.trim() } : {}),
      ...(spec.options ? { config: { options } } : {}),
    };
    try {
      // Pick a free key; retry with a suffix if the server reports a clash
      // (e.g. a same-named field already in the shared library).
      let n = 1;
      let key = base;
      while (taken.has(key)) key = `${base}_${++n}`;
      for (let attempt = 0; attempt < 6; attempt++) {
        try {
          const r = await api.post(`/entity-types/${entityTypeId}/fields`, { ...body, key });
          toast.success(`Column "${body.label}" added`);
          onCreated && onCreated(r.data);
          onOpenChange(false);
          return;
        } catch (err) {
          if (err?.response?.status !== 409) throw err;
          key = `${base}_${++n}`;
        }
      }
      throw new Error("Couldn't find a free name for this column — try a different name.");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="add-column-dialog">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle>Add column</DialogTitle>
            <DialogDescription>
              Adds a new field. It shows up as a column right away and in the add/edit form.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="col-name">Column name</Label>
              <Input id="col-name" autoFocus value={label} onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. Book No, Rub test, Supplier" data-testid="add-column-name" />
            </div>
            <div>
              <Label>Type</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger data-testid="add-column-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {COLUMN_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value} data-testid={`add-column-type-${t.value}`}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {spec.unit && (
              <div>
                <Label htmlFor="col-unit">Unit <span className="text-muted-foreground font-normal">(optional)</span></Label>
                <Input id="col-unit" value={unit} onChange={(e) => setUnit(e.target.value)}
                  placeholder="e.g. cm, GSM, m" data-testid="add-column-unit" />
              </div>
            )}
            {spec.options && (
              <div>
                <Label htmlFor="col-options">Choices</Label>
                <textarea id="col-options" rows={4} value={optionsText}
                  onChange={(e) => setOptionsText(e.target.value)}
                  placeholder={"One per line, or comma-separated\nCurtains, Blinds, Upholstery"}
                  className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm"
                  data-testid="add-column-options" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={!canSave} data-testid="add-column-save">
              {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Add column
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
